import { Close, Download } from "@mui/icons-material"
import { Box, CardContent, Chip, IconButton, Tooltip, Typography } from "@mui/material"
import type React from "react"
import { memo, useMemo } from "react"
import SpriteIcon from "../../core-components/assets/Icon"
import RenderCsvContent from "./renderCsvContent"
import { getIconTypeFromMime } from "../../utilities/helpers"

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"]
const VIDEO_EXTENSIONS = ["mp4", "webm", "ogg"]
const AUDIO_EXTENSIONS = ["mp3", "wav", "ogg"]
const OFFICE_EXTENSIONS = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"]
const CSV_EXTENSIONS = ["csv", "log"]
const TEXT_EXTENSIONS = ["txt"]

const getFileExtension = (fileName: string): string => {
  return fileName?.split(".").pop()?.toLowerCase() || ""
}

const getFileIcon = (fileExtension: string) => {
  const iconName = getIconTypeFromMime(fileExtension) || "file_icon"
  return <SpriteIcon id={iconName} width="26" height="26" />
}

const getActualFileName = (fileName?: string, fileUrl?: string): string => {
  return fileName || fileUrl?.split("/").pop() || "Unknown file"
}

const isImageFile = (mimeType: string, extension: string): boolean => {
  return mimeType?.startsWith("image") || IMAGE_EXTENSIONS.includes(extension)
}

const isVideoFile = (mimeType: string, extension: string): boolean => {
  return mimeType?.startsWith("video") || VIDEO_EXTENSIONS.includes(extension)
}

const isAudioFile = (mimeType: string, extension: string): boolean => {
  return mimeType?.startsWith("audio") || AUDIO_EXTENSIONS.includes(extension)
}

const isPdfFile = (mimeType: string, extension: string): boolean => {
  return mimeType === "application/pdf" || extension === "pdf"
}

const isOfficeFile = (extension: string): boolean => {
  return OFFICE_EXTENSIONS.includes(extension)
}

const isCsvFile = (mimeType: string, extension: string): boolean => {
  return mimeType?.startsWith("text") || CSV_EXTENSIONS.includes(extension)
}

const isTextFile = (extension: string): boolean => {
  return TEXT_EXTENSIONS.includes(extension)
}

const handleDownload = (fileUrl: string) => {
  if (fileUrl) {
    window.open(fileUrl, "_blank")
  }
}

const renderImagePreview = (fileUrl: string, fileName: string) => (
  <img src={fileUrl || "/placeholder.svg"} height='500px' alt={fileName} style={{ maxWidth: "100%" }} />
)

const renderVideoPreview = (fileUrl: string, maxHeight: number | string) => (
  <video controls height='500px' style={{ width: "100%", maxHeight: maxHeight }} src={fileUrl} />
)

const renderAudioPreview = (fileUrl: string) => <audio controls src={fileUrl} style={{ width: "100%" }} />

const renderPdfPreview = (fileUrl: string, maxHeight: number | string) => (
  <iframe
    src={fileUrl}
    width="100%"
    height="500px"
    title="PDF Preview"
    style={{ border: "none", maxHeight: maxHeight }}
  />
)

const renderTextPreview = (fileUrl: string, maxHeight: number | string) => (
  <iframe
    src={fileUrl}
    width="100%"
    height="500px"
    title="Text Preview"
    style={{ border: "none", maxHeight: maxHeight }}
  />
)

const renderCsvPreview = (fileUrl: string, mimeType: string) => (
  <Box height="500px">
    <RenderCsvContent fileUrl={fileUrl} mimeType={mimeType} />
  </Box>
)

const renderOfficePreview = (fileUrl: string) => (
  <iframe
    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
    width="100%"
    height="500px"
    title="Office Preview"
    style={{ border: "none" }}
  />
)

const renderUnsupportedPreview = () => (
  <div
    style={{
      color: "#cc0000",
      padding: "1em",
      background: "#fff3f3",
      border: "1px solid #f5c2c7",
    }}
  >
    <strong>Preview not supported for unsupported/local files.</strong>
    <br />
    Please upload the file to a public URL to preview it here.
  </div>
)

type FilePreviewerProps = {
  fileUrl: string
  mimeType: string
  fileName?: string
  showDownload?: boolean
  showFullscreen?: boolean
  onClose?: () => void
  maxWidth?: number | string
  maxHeight?: number | string
  height: number | string
  csvProps?: {
    sx?: React.CSSProperties
  }
}

const FilePreviewer: React.FC<FilePreviewerProps> = memo(
  ({
    fileUrl,
    mimeType,
    fileName,
    showDownload = true,
    onClose,
    maxWidth = "100%",
    maxHeight = "100%",
    height,
    csvProps = {},
  }) => {
    const fileData = useMemo(() => {
      const extension = getFileExtension(fileName || fileUrl)
      const actualFileName = getActualFileName(fileName, fileUrl)

      return {
        extension,
        actualFileName,
        isImage: isImageFile(mimeType, extension),
        isVideo: isVideoFile(mimeType, extension),
        isAudio: isAudioFile(mimeType, extension),
        isPdf: isPdfFile(mimeType, extension),
        isOffice: isOfficeFile(extension),
        isCSV: isCsvFile(mimeType, extension),
        isText: isTextFile(extension),
      }
    }, [fileUrl, mimeType, fileName])

    const fileIcon = useMemo(() => getFileIcon(mimeType), [mimeType])

    const onDownloadClick = useMemo(() => () => handleDownload(fileUrl), [fileUrl])

    const renderPreview = useMemo(() => {
      const { isImage, isVideo, isAudio, isPdf, isOffice, isCSV, isText } = fileData

      if (isImage) {
        return renderImagePreview(fileUrl, fileData.actualFileName)
      }
      if (isVideo) {
        return renderVideoPreview(fileUrl, maxHeight)
      }
      if (isAudio) {
        return renderAudioPreview(fileUrl)
      }
      if (isPdf) {
        return renderPdfPreview(fileUrl, maxHeight)
      }
      if (isText) {
        return renderTextPreview(fileUrl, maxHeight)
      }
      if (isCSV) {
        return renderCsvPreview(fileUrl, mimeType)
      }
      if (isOffice) {
        return renderOfficePreview(fileUrl)
      }

      return renderUnsupportedPreview()
    }, [fileData, fileUrl, mimeType, maxHeight])

    return (
      <Box
        sx={{
          maxWidth,
          maxHeight,
          overflow: "auto",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {fileIcon}
            <Typography variant="subtitle1" noWrap sx={{ maxWidth: 300 }}>
              {fileData.actualFileName}
            </Typography>
            <Chip label={`.${fileData.extension}`} size="small" variant="outlined" color="primary" />
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            {showDownload && (
              <Tooltip title="Download">
                <IconButton size="small" onClick={onDownloadClick}>
                  <Download />
                </IconButton>
              </Tooltip>
            )}
            {onClose && (
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose}>
                  <Close />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <CardContent sx={{ height: "100%" }}>{renderPreview}</CardContent>
      </Box>
    )
  },
)

export default FilePreviewer
